import { createFileRoute, Outlet, Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  LayoutDashboard,
  Building2,
  Calculator,

  Home as HomeIcon,
  Users,
  ClipboardList,
  CheckSquare,
  FileText,
  UserCircle,
  Megaphone,
  Bell,
  Menu,
  X,
  LogOut,
  ExternalLink,
} from "lucide-react";

type NavItem = {
  to: "/admin" | "/admin/propiedades" | "/admin/crm" | "/admin/contactos" | "/admin/tareas" | "/admin/blog" | "/admin/busquedas" | "/admin/perfil" | "/admin/zuba" | "/admin/calculadora" | "/admin/influencer";
  label: string;
  icon: typeof LayoutDashboard;
  exact?: boolean;
};

const NAV: NavItem[] = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { to: "/admin/propiedades", label: "Propiedades", icon: HomeIcon },
  { to: "/admin/zuba", label: "Disponibilidad ZUBA", icon: Building2 },
  { to: "/admin/calculadora", label: "Calculadora ZUBA", icon: Calculator },
  { to: "/admin/crm", label: "CRM", icon: ClipboardList },
  { to: "/admin/contactos", label: "Contactos", icon: Users },
  { to: "/admin/tareas", label: "Tareas", icon: CheckSquare },
  { to: "/admin/blog", label: "Blog", icon: FileText },
  { to: "/admin/busquedas", label: "Alertas", icon: Bell },
  { to: "/admin/influencer", label: "Influencer DM", icon: Megaphone },
  { to: "/admin/perfil", label: "Perfil", icon: UserCircle },
];


export const Route = createFileRoute("/_authenticated/admin")({
  component: AdminLayout,
});

function AdminLayout() {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  const signOut = async () => {
    await supabase.auth.signOut();
    await navigate({ to: "/auth" });
  };

  const isActive = (to: string, exact?: boolean) =>
    exact ? pathname === to : pathname === to || pathname.startsWith(to + "/");

  return (
    <div className="flex min-h-screen bg-secondary/40">
      {/* Sidebar */}
      <aside className="hidden md:flex md:w-64 md:flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground">
        <div className="flex h-16 items-center gap-2 px-5 border-b border-sidebar-border">
          <span className="grid h-9 w-9 place-items-center rounded-md bg-accent text-accent-foreground font-serif text-lg font-bold">
            D
          </span>
          <div className="leading-tight">
            <div className="text-sm font-semibold">Dahiana Marín</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-sidebar-foreground/60">
              Admin
            </div>
          </div>
        </div>

        <nav className="flex-1 space-y-1 p-3">
          {NAV.map((item) => {
            const active = isActive(item.to, item.exact);
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition ${
                  active
                    ? "bg-sidebar-accent text-sidebar-accent-foreground"
                    : "text-sidebar-foreground/85 hover:bg-sidebar-accent/60"
                }`}
              >
                <Icon className="h-4 w-4" /> {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="border-t border-sidebar-border p-3">
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            className="mb-1 flex items-center gap-2 rounded-md px-3 py-2 text-xs text-sidebar-foreground/70 hover:bg-sidebar-accent/60"
          >
            <ExternalLink className="h-3.5 w-3.5" /> Ver sitio público
          </a>
          <button
            onClick={signOut}
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-sidebar-foreground/85 hover:bg-sidebar-accent/60"
          >
            <LogOut className="h-4 w-4" /> Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="flex flex-1 flex-col">
        <header className="md:hidden flex h-14 items-center justify-between border-b border-border bg-background px-4">
          <Link to="/admin" className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-md bg-primary text-primary-foreground font-serif font-bold">
              D
            </span>
            <span className="text-sm font-semibold">Admin</span>
          </Link>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-border"
            aria-label={menuOpen ? "Cerrar menú" : "Abrir menú"}
          >
            {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </header>

        {/* Mobile drawer */}
        {menuOpen && (
          <div className="md:hidden border-b border-border bg-background">
            <nav className="flex flex-col p-3">
              {NAV.map((item) => {
                const active = isActive(item.to, item.exact);
                const Icon = item.icon;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setMenuOpen(false)}
                    className={`flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium transition ${
                      active
                        ? "bg-primary/10 text-primary"
                        : "text-foreground/85 hover:bg-secondary"
                    }`}
                  >
                    <Icon className="h-4 w-4" /> {item.label}
                  </Link>
                );
              })}
              <button
                onClick={signOut}
                className="mt-2 flex items-center gap-3 rounded-md px-3 py-3 text-sm font-medium text-foreground/85 hover:bg-secondary"
              >
                <LogOut className="h-4 w-4" /> Cerrar sesión
              </button>
            </nav>
          </div>
        )}

        <main className="flex-1 p-6 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
